import { Component, Fragment, Suspense, useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useMe } from '@/lib/hooks';
import { navigate } from '@/lib/router';
import { ADMIN_GROUPS, adminSection, adminSectionList, type AdminSection } from '@/lib/slots';
import { ViewBoundary } from '@/shell/ViewHost';
import { Empty, Loading } from '@/ui/bits';
import { Icon } from '@/ui/Icon';

// The super admin console: the section navigation on the left (grouped, with attention dots) and the open
// section on the right. The open section lives in the address bar as ?admin=<id>.

const MAIN_ID = 'admMain';

export function Console({ section }: { section: string }) {
  const me = useMe();
  const sections = adminSectionList();
  const current = adminSection(section);
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [section]);

  useEffect(() => {
    const before = document.title;
    document.title = `${current?.label ?? 'Section not found'} · Admin console · Clientbook`;
    return () => {
      document.title = before;
    };
  }, [current]);

  const onNavKey = (e: KeyboardEvent<HTMLElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const links = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('.adm-link')];
    const at = links.indexOf(document.activeElement as HTMLButtonElement);
    if (at < 0) return;
    e.preventDefault();
    const last = links.length - 1;
    const next =
      e.key === 'Home' ? 0 : e.key === 'End' ? last : e.key === 'ArrowDown' || e.key === 'ArrowRight' ? (at === last ? 0 : at + 1) : at === 0 ? last : at - 1;
    links[next]?.focus();
  };

  return (
    <div className="admin">
      <nav className="adm-nav" aria-label="Admin sections" onKeyDown={onNavKey}>
        <div className="ah">
          <span className="shield">
            <Icon name="shield" />
          </span>
          <div>
            <b>Admin console</b>
            <span>Super admin · {me.user.name}</span>
          </div>
        </div>
        {ADMIN_GROUPS.map((group) => {
          const items = sections.filter((s) => s.group === group);
          if (items.length === 0) return null;
          return (
            <Fragment key={group}>
              <div className="grp" aria-hidden="true">
                {group}
              </div>
              {items.map((s) => (
                <NavLink key={s.id} section={s} active={s.id === current?.id} group={group} />
              ))}
            </Fragment>
          );
        })}
      </nav>
      <div className="adm-main" id={MAIN_ID} ref={mainRef}>
        {current ? (
          <section key={current.id} className="adm-sec on" id={`sec-${current.id}`} aria-label={current.label}>
            <ViewBoundary label={current.label}>
              <Suspense fallback={<Loading label={`Loading ${current.label}`} />}>
                <current.component />
              </Suspense>
            </ViewBoundary>
          </section>
        ) : (
          <section className="adm-sec on" aria-label="Section not found">
            <Empty
              icon="shield"
              title="Section not found"
              action={
                <button type="button" className="btn btn-primary" onClick={() => navigate({ admin: 'team' }, { replace: true })}>
                  <Icon name="users" />
                  Go to Team &amp; access
                </button>
              }
            >
              The address asks for a section called “{section}”. Pick a section from the list, or start with the team.
            </Empty>
          </section>
        )}
      </div>
    </div>
  );
}

function NavLink({ section, active, group }: { section: AdminSection; active: boolean; group: string }) {
  return (
    <button
      type="button"
      className="adm-link"
      data-sec={section.id}
      aria-current={active ? 'true' : undefined}
      aria-controls={MAIN_ID}
      data-group={group}
      onClick={() => {
        if (!active) navigate({ admin: section.id }, { replace: true });
      }}
    >
      <Icon name={section.icon} />
      {section.label}
      {section.useAttention ? (
        <DotBoundary>
          <AttentionDot use={section.useAttention} />
        </DotBoundary>
      ) : null}
    </button>
  );
}

/** Calls the section's own attention hook; a dot shows while it returns true. */
function AttentionDot({ use }: { use: () => boolean }) {
  const on = use();
  return on ? <span className="warn-dot" role="img" aria-label="Needs attention" /> : null;
}

/** A failing attention hook from any area hides its dot and leaves the navigation working. */
class DotBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: Error): void {
    console.error('[admin] an attention dot failed', error);
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}
